// Admin tickets API — typed wrappers around /admin/v1/tickets/*.

import type { ApiClient } from '../api-client.js';

export type TicketStatus =
  | 'open'
  | 'in_progress'
  | 'waiting_customer'
  | 'resolved'
  | 'closed';

export type TicketStatusFilter = 'all' | TicketStatus;
export type TicketAssignedFilter = 'all' | 'me' | 'unassigned';

export interface TicketRow {
  id: string;
  tenantId: string;
  tenantName: string;
  createdBy: string;
  subject: string;
  status: TicketStatus;
  priority: string;
  planCodeAtCreation: string;
  lastMessageAt: string;
  resolvedAt: string | null;
  closedAt: string | null;
  assignedStaffId: string | null;
  createdAt: string;
  updatedAt: string;
  lastAuthorIsCustomer: boolean;
}

export interface TicketMessage {
  id: string;
  ticketId: string;
  authorKind: 'customer' | 'staff';
  authorId: string | null;
  body: string;
  createdAt: string;
}

export interface TicketDetail extends TicketRow {
  messages: TicketMessage[];
  tenantPlanCode: string | null;
  tenantFrozenAt: string | null;
}

export interface ListTicketsParams {
  status?: TicketStatusFilter;
  assigned?: TicketAssignedFilter;
  tenantId?: string;
  hasCustomerReply?: boolean;
  limit?: number;
}

export const TICKETS_LIST_QUERY_KEY = ['admin', 'tickets'] as const;
export const ticketQueryKey = (id: string): readonly unknown[] =>
  ['admin', 'tickets', id] as const;

function buildQuery(params: ListTicketsParams): string {
  const sp = new URLSearchParams();
  if (params.status && params.status !== 'all') sp.set('status', params.status);
  if (params.assigned && params.assigned !== 'all') sp.set('assigned', params.assigned);
  if (params.tenantId) sp.set('tenantId', params.tenantId);
  if (params.hasCustomerReply !== undefined) {
    sp.set('hasCustomerReply', params.hasCustomerReply ? 'true' : 'false');
  }
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  const tail = sp.toString();
  return tail.length > 0 ? `?${tail}` : '';
}

export const ticketsApi = {
  async list(client: ApiClient, params: ListTicketsParams = {}): Promise<{ rows: TicketRow[] }> {
    return client.get<{ rows: TicketRow[] }>(`/tickets${buildQuery(params)}`);
  },
  async detail(client: ApiClient, id: string): Promise<TicketDetail> {
    return client.get<TicketDetail>(`/tickets/${id}`);
  },
  async reply(client: ApiClient, id: string, body: string): Promise<TicketMessage> {
    return client.post<TicketMessage>(`/tickets/${id}/messages`, { body });
  },
  async patch(
    client: ApiClient,
    id: string,
    patch: { status?: TicketStatus; assignedStaffId?: string | null },
  ): Promise<TicketRow> {
    return client.patch<TicketRow>(`/tickets/${id}`, patch);
  },
};

// Display helpers shared between list + detail surfaces.
export function statusLabel(s: TicketStatus): string {
  switch (s) {
    case 'open':
      return 'Open';
    case 'in_progress':
      return 'In progress';
    case 'waiting_customer':
      return 'Waiting on customer';
    case 'resolved':
      return 'Resolved';
    case 'closed':
      return 'Closed';
  }
}

// Variant for the shadcn Badge. Pinned to the same alphabet across
// list + detail so the same status reads identically everywhere.
export function statusBadgeVariant(
  s: TicketStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (s) {
    case 'open':
      return 'destructive';
    case 'in_progress':
      return 'default';
    case 'waiting_customer':
      return 'secondary';
    case 'resolved':
      return 'outline';
    case 'closed':
      return 'outline';
  }
}
